/**
 * Session token storage (``local_users``) + claim decoding (every backend).
 *
 * Tokens are JWTs — HS256 from ``POST /api/v1/auth/login``, or EdDSA from
 * Neon Auth. The browser cannot verify the signature and must not pretend
 * to: claims are read only to drive the UI (who am I, show
 * admin links, when to ask for a new login). The backend re-verifies the
 * token on every request, so tampering with localStorage gains nothing.
 *
 * Why localStorage and not an httpOnly cookie: the WebSocket handshake and
 * the Electron/Capacitor shells all send ``Authorization: Bearer``, so one
 * mechanism covers every client. The cost is XSS exposure; the CSP and
 * the no-inline-script rule are the mitigation.
 */

export const TOKEN_STORAGE_KEY = "omni.session.token";
const KEY = TOKEN_STORAGE_KEY;

export interface TokenClaims {
  sub: string;
  email: string | null;
  role: string | null;
  exp: number;
}

export function loadToken(): string | null {
  try {
    return window.localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function saveToken(token: string): void {
  try {
    window.localStorage.setItem(KEY, token);
  } catch {
    /* storage blocked (private window): the session lasts until reload */
  }
}

export function clearToken(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* nothing to clear */
  }
}

/** Decode the payload of a JWT without verifying it. Null if malformed. */
export function decodeClaims(token: string): TokenClaims | null {
  const part = token.split(".")[1];
  if (!part) return null;
  try {
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
    const raw = JSON.parse(json) as Record<string, unknown>;
    if (typeof raw.sub !== "string" || typeof raw.exp !== "number") return null;
    return {
      sub: raw.sub,
      email: typeof raw.email === "string" ? raw.email : null,
      role: typeof raw.role === "string" ? raw.role : null,
      exp: raw.exp,
    };
  } catch {
    return null;
  }
}

/** True when the token is missing, malformed, or past ``exp``. */
export function isExpired(token: string, nowMs: number = Date.now()): boolean {
  const claims = decodeClaims(token);
  return claims === null || claims.exp * 1000 <= nowMs;
}
